import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { RefreshCw, ArrowLeft, Clock, AlertCircle } from 'lucide-react'
import { config } from '@workspace/public-utils'
import { Seo } from '../../components/Seo'

interface NumberReappearance {
  number: number
  last_appearance_round: number
  last_appearance_date: string
  days_since_last: number
  average_cycle: number
  appearance_count: number
  comeback_score: number
}

const ReappearanceAnalysisPage = () => {
  const [selectedRange, setSelectedRange] = useState(0) // 0 = 10, 1 = 30, 2 = 50
  const [analysisData, setAnalysisData] = useState<Record<number, any>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ranges = [10, 30, 50]
  const scopeMap = ['recent_10', 'recent_30', 'recent_50']

  useEffect(() => {
    fetchAnalysis(ranges[selectedRange])
  }, [selectedRange])

  const fetchAnalysis = async (range: number) => {
    try {
      setLoading(true)
      setError(null)

      const scope = scopeMap[selectedRange]
      const response = await fetch(
        `${config.API_BASE_URL}/api/v1/public/analysis/reappearance-cycle?scope=${scope}`
      )

      if (!response.ok) {
        throw new Error('분석 데이터를 불러오는데 실패했습니다.')
      }

      const data = await response.json()
      
      if (!data.reappearance_data || data.reappearance_data.length === 0) {
        throw new Error('분석할 데이터가 없습니다. 로또 결과가 충분히 쌓이면 분석이 표시됩니다.')
      }

      // Convert API data to frontend interface
      const reappearanceData: NumberReappearance[] = data.reappearance_data.map((item: any) => ({
        number: item.number,
        last_appearance_round: item.last_appearance_round || 0,
        last_appearance_date: item.last_appearance_date || '',
        days_since_last: item.rounds_since_last !== null ? item.rounds_since_last : 999,
        average_cycle: item.average_cycle || 0,
        appearance_count: item.appearance_count,
        comeback_score: item.comeback_score
      }))

      const comebackSignals: NumberReappearance[] = data.comeback_signals.map((item: any) => ({
        number: item.number,
        last_appearance_round: item.last_appearance_round || 0,
        last_appearance_date: item.last_appearance_date || '',
        days_since_last: item.rounds_since_last !== null ? item.rounds_since_last : 999,
        average_cycle: item.average_cycle || 0,
        appearance_count: item.appearance_count,
        comeback_score: item.comeback_score
      }))

      setAnalysisData((prev) => ({
        ...prev,
        [range]: {
          reappearanceData,
          total_analyzed: data.total_analyzed,
          comeback_signals: comebackSignals
        }
      }))
    } catch (err) {
      console.error('Error fetching analysis:', err)
      setError(err instanceof Error ? err.message : '데이터를 불러올 수 없습니다.')
    } finally {
      setLoading(false)
    }
  }

  const currentData = analysisData[ranges[selectedRange]]

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'from-red-500 to-orange-500'
    if (score >= 60) return 'from-orange-500 to-yellow-500'
    if (score >= 40) return 'from-yellow-500 to-green-500'
    return 'from-green-500 to-blue-500'
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      <Seo
        title="재출현 주기 분석"
        description="각 번호의 마지막 출현일과 평균 재출현 주기를 기반으로 '컴백 임박' 번호를 확인해 보세요."
        canonicalPath="/analysis/reappearance"
      />
      {/* Header */}
      <section className="bg-gradient-to-br from-green-500 via-emerald-500 to-green-600 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/analysis" className="inline-flex items-center text-white hover:text-white/80 mb-6 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            분석 방법으로 돌아가기
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center mb-4">
              <RefreshCw className="w-12 h-12 text-white mr-4" />
              <h1 className="text-4xl md:text-6xl font-black text-white">
                재출현 주기 분석
              </h1>
            </div>
            <p className="text-xl text-white/90 text-center max-w-3xl mx-auto">
              각 번호별 마지막 출현일과 평균 재출현 주기를 확인하여 컴백 임박 신호를 찾아보세요
            </p>
          </motion.div>
        </div>
      </section>

      {/* Range Selector */}
      <section className="py-8 px-4 bg-white shadow-sm dark:bg-gray-900 dark:shadow-none dark:border-b dark:border-gray-800 transition-colors">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">분석 기간 선택</h2>
            <div className="flex gap-2">
              {ranges.map((range, index) => (
                <button
                  key={range}
                  onClick={() => setSelectedRange(index)}
                  className={`px-6 py-3 rounded-lg font-bold transition-all duration-300 ${
                    selectedRange === index
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg scale-105'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  최근 {range}회
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-green-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-red-50 dark:bg-red-950/40 border-l-4 border-red-500 p-6 rounded-lg">
            <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Analysis Content */}
      {!loading && !error && currentData && (
        <div className="max-w-7xl mx-auto px-4 py-12">
          {/* Comeback Signals */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="flex items-center mb-8">
              <AlertCircle className="w-8 h-8 text-red-500 mr-3" />
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                컴백 임박 신호 (Comeback Signal)
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {currentData.comeback_signals.map((data: NumberReappearance) => (
                <motion.div
                  key={data.number}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05 }}
                  className={`bg-gradient-to-br ${getScoreColor(data.comeback_score)} rounded-2xl p-6 text-white shadow-lg text-center`}
                >
                  <div className="text-5xl font-black mb-2">{data.number}</div>
                  <div className="text-sm opacity-90 mb-1">점수: {data.comeback_score.toFixed(0)}</div>
                  <div className="text-xs opacity-80">
                    {data.days_since_last}회 전 출현
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* All Numbers Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg dark:shadow-none dark:border dark:border-gray-800"
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              전체 번호 재출현 분석
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-3 text-left font-bold text-gray-700 dark:text-gray-300">번호</th>
                    <th className="px-4 py-3 text-left font-bold text-gray-700 dark:text-gray-300">출현 횟수</th>
                    <th className="px-4 py-3 text-left font-bold text-gray-700 dark:text-gray-300">마지막 출현</th>
                    <th className="px-4 py-3 text-left font-bold text-gray-700 dark:text-gray-300">경과 회차</th>
                    <th className="px-4 py-3 text-left font-bold text-gray-700 dark:text-gray-300">평균 주기</th>
                    <th className="px-4 py-3 text-left font-bold text-gray-700 dark:text-gray-300">컴백 점수</th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.reappearanceData.map((data: NumberReappearance) => (
                    <tr key={data.number} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-4 py-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 text-white font-bold flex items-center justify-center">
                          {data.number}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{data.appearance_count}회</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {data.last_appearance_round > 0 
                          ? `${data.last_appearance_round}회`
                          : '없음'}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {data.days_since_last < 999 ? `${data.days_since_last}회` : '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {data.average_cycle > 0 ? `${data.average_cycle.toFixed(1)}회` : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                            <div
                              className={`bg-gradient-to-r ${getScoreColor(data.comeback_score)} h-full transition-all duration-500`}
                              style={{ width: `${data.comeback_score}%` }}
                            />
                          </div>
                          <span className="text-sm font-bold text-gray-700 dark:text-gray-300 w-10">
                            {data.comeback_score.toFixed(0)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Legend */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 bg-blue-50 dark:bg-blue-950/40 rounded-2xl p-6 border border-transparent dark:border-blue-900/50"
          >
            <h4 className="text-lg font-bold text-blue-900 dark:text-blue-100 mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              컴백 점수 계산 방법
            </h4>
            <p className="text-gray-700 dark:text-gray-300 mb-3">
              컴백 점수는 마지막 출현 이후 경과 회차와 평균 재출현 주기를 비교하여 계산됩니다.
              경과 회차가 평균 주기에 가까울수록 높은 점수를 받습니다.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gradient-to-r from-red-500 to-orange-500"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">80-100: 매우 높음</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gradient-to-r from-orange-500 to-yellow-500"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">60-80: 높음</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gradient-to-r from-yellow-500 to-green-500"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">40-60: 보통</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gradient-to-r from-green-500 to-blue-500"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">0-40: 낮음</span>
              </div>
            </div>
          </motion.div>

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-8 bg-yellow-50 dark:bg-yellow-950/30 border-l-4 border-yellow-500 p-6 rounded-lg"
          >
            <p className="text-gray-700 dark:text-gray-300">
              <strong className="text-yellow-700 dark:text-yellow-400">⚠️ 안내:</strong> 이 분석은 과거 당첨 번호의 통계적 분석이며, 
              미래 당첨 번호를 예측하거나 보장하지 않습니다. 
              로또는 무작위 추첨이므로 모든 번호는 동일한 당첨 확률을 가집니다.
            </p>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default ReappearanceAnalysisPage

